import React, { useRef, useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Button,
  Divider,
  Stack,
} from "@mui/material";
import StarIcon from '@mui/icons-material/Star';
// Removed MUI chevrons in favor of Font Awesome chevrons
import Bookmark from './bookmark.svg';
import BookmarkFilled from './bookmark-filled.svg';
import Location from './location.svg';
import Audio from './audio.svg';
import AudioSilent from './audio-silent.svg';
// Removed MUI left chevron; using Font Awesome instead
import { Stop } from "@app/shared/models/tour.type";
import { useTranslation } from "react-i18next";
import { useSetRecoilState, useRecoilCallback } from "recoil";
import { atom_activeStopState } from "@app/shared/state/active-stop.atom";
import { atom_openedStopState } from "@app/shared/state/opened-stop.atom";
import { atom_showModalContent, atom_navigationHelperState } from "@app/shared/state/show-content-modal.atom";
import { atom_bookmarkedStopsState, atom_allBookmarksState } from "@app/shared/state/filter.atom";
import { atom_tourState, atom_isDesktop } from "@app/shared/state/tour.atom";
import { atom_listModeState } from "@app/shared/state/list-mode.atom";
import { extractStopLinks } from "@app/shared/hooks/extract-stop-links";
import { atom_modalType } from "@app/shared/state/show-content-modal.atom";
import { useRenderTracker } from '@app/shared/debug';
import { service_tourService } from "@app/shared/services/tours.service";
import { passiveCollector, isWithinRadius } from "@app/shared/services/passive-data/passive-data-collector";
import { atom_geolocationState } from "@app/shared/state/geolocation.atom";
import { mediaProgressStore } from "@app/shared/services/media-progress-store";
import DirectionIcon from './direction.svg';


// Static sx objects — allocated once, shared across all MapCard instances
const SX_OUTER_BOX = { width: '100%', mb: 'auto', mt: 'auto', overflow: 'hidden' } as const;
const SX_CONTENT_ROW = { display: 'flex', width: '100%', gap: 2 } as const;
const SX_CONTENT_COL = { flex: 1, display: 'flex', flexDirection: 'column' } as const;
const SX_DESCRIPTION = { mt: '6px', display: '-webkit-box', overflow: 'hidden', wordBreak: 'break-word', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2, lineHeight: 1.5 } as const;
const SX_CATEGORY_LABEL = { fontWeight: 700, fontStyle: 'normal', fontSize: '12px', lineHeight: '16px', letterSpacing: '0px' } as const;
const SX_DOT_SEPARATOR = { bgcolor: '#48526B', width: '3px', height: '3px', borderRadius: '50%', opacity: 1, display: 'flex', justifyContent: 'center', ml: '1px', mr: '1px' } as const;
const SX_DISTANCE_DOT = { bgcolor: '#48526B', width: 2, height: 2, borderRadius: '50%', opacity: 1, display: 'inline-block', ml: '10px', mr: '10px' } as const;
const SX_CATEGORIES_STACK = { mt: 'auto', flexWrap: 'wrap', gap: '4px', paddingTop: '5px' } as const;
const SX_STAR = { fontSize: 12, color: '#FFC107' } as const;
const SX_DIVIDER = { width: '100%', borderColor: '#D5D9E2', borderBottomWidth: '1.5px' } as const;
const SX_CARD_ACTIONS = { px: 1, display: 'flex', justifyContent: 'space-between', width: '100%', height: 'auto' } as const;

interface MapCardProps {
  title: string;
  description?: string;
  image: string;
  categories?: string[];
  distance?: string;
  rating?: number;
  minimal?: boolean;
  onSelect?: ((stop: Stop) => void) | (() => void);
  isSelected?: boolean;
  activeStop?: Stop;
  // onFlyTo?: (stop: Stop) => void;
  isListMode?: boolean;
}

export const MapCard = React.memo(function MapCard({
  title,
  description,
  image,
  categories,
  distance,
  rating,
  onSelect,
  isSelected = false,
  activeStop,
  // onFlyTo,
  isListMode
}: MapCardProps) {
  useRenderTracker('MapCard');
  const { t, i18n } = useTranslation();
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const isHebrew = () => i18n.language === 'he';
  const [audioDuration, setAudioDuration] = useState<string>('');
  const [audioCurrentTime, setAudioCurrentTime] = useState<string>('');
  const linksElements = extractStopLinks(activeStop, activeStop?.s_title);

  // Zero Recoil subscriptions — all read on-demand to prevent re-renders
  const setState_activeStopState = useSetRecoilState(atom_activeStopState);
  const setState_showModal = useSetRecoilState(atom_showModalContent);
  const setState_modalType = useSetRecoilState(atom_modalType);
  const setNavState = useSetRecoilState(atom_navigationHelperState);
  const setListMode = useSetRecoilState(atom_listModeState);
  const setAllBookmarks = useSetRecoilState(atom_allBookmarksState);

  const getAtoms = useRecoilCallback(({ snapshot }) => () => ({
    activeStopState: snapshot.getLoadable(atom_activeStopState).getValue(),
    geolocation: snapshot.getLoadable(atom_geolocationState).getValue(),
    allBookmarks: snapshot.getLoadable(atom_allBookmarksState).getValue(),
    bookmarkedStops: snapshot.getLoadable(atom_bookmarkedStopsState).getValue() as string[],
    tourState: snapshot.getLoadable(atom_tourState).getValue(),
    isDesktop: snapshot.getLoadable(atom_isDesktop).getValue(),
  }));

  const setOpenedStop = useRecoilCallback(({ set }) => (stop: Stop) => {
    set(atom_openedStopState, stop);
  });

  // Read once on mount, store in refs (these don't change or change rarely)
  const isDesktopRef = useRef(false);
  const titleColorRef = useRef('#D0552E');
  const initializedRef = useRef(false);
  if (!initializedRef.current) {
    const atoms = getAtoms();
    isDesktopRef.current = atoms.isDesktop;
    titleColorRef.current = activeStop?.config?.color || atoms.tourState?.config?.['titleColor'] || '#D0552E';
    if (activeStop) {
      setIsBookmarked(atoms.bookmarkedStops.includes(activeStop.id));
    }
    initializedRef.current = true;
  }
  const isDesktop = isDesktopRef.current;
  const hebrew = isHebrew();
  const titleColor = titleColorRef.current;
  // Memoized sx objects — stable across renders unless dependencies change
  const sxHeaderBox = useMemo(() => ({
    display: 'flex',
    height: isDesktop ? '26px' : '21px',
    minWidth: isDesktop ? '182px' : '160px',
    width: 'auto',
    maxWidth: '100%',
    fontSize: '12px',
    color: 'white',
    fontWeight: 'normal'
  }), [isDesktop]);

  const sxTitleBg = useMemo(() => ({
    display: 'flex',
    alignItems: 'center',
    borderRadius: hebrew ? '8px 8px 0 0' : '8px 8px 0 0px',
    bgcolor: titleColor,
    width: 'auto',
    minWidth: isDesktop ? '200px' : '180px',
    height: '100%',
    textAlign: hebrew ? 'right' as const : 'left' as const,
  }), [isDesktop, hebrew, titleColor]);

  const sxTitleText = useMemo(() => ({
    padding: '0 10px',
    fontWeight: 400,
    fontStyle: 'normal',
    lineHeight: '16px',
    fontSize: isDesktop ? '14px' : '12px',
    letterSpacing: '0px'
  }), [isDesktop]);

  const sxCard = useMemo(() => ({
    minHeight: isListMode ? '155px' : 'auto',
    maxHeight: 'auto',
    display: 'flex',
    padding: 'var(--sds-size-space-400) 16px var(--sds-size-space-200) 16px',
    flexDirection: 'column' as const,
    alignItems: 'flex-start',
    gap: 'var(--sds-size-space-200)',
    alignSelf: 'stretch',
    borderRadius: hebrew ? '8px 0px 8px 8px' : '0px 8px 8px 8px',
    background: 'var(--white, #FFF)',
    boxShadow: isSelected
      ? '0px 3px 14px 0px rgba(0, 0, 0, 0.15)'
      : '0px 3px 14px 0px rgba(0, 0, 0, 0.15), 0px 12px 24px -8px rgba(0,0,0,0.18)',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    ...(isSelected ? { border: `1px solid ${titleColor}` } : {}),
  }), [isListMode, hebrew, isSelected, titleColor]);

  const sxCardContent = useMemo(() => ({
    pt: '8px',
    pb: isDesktop ? '14px' : '4px',
    px: '8px',
    width: '100%',
    height: 'auto',
    flex: 1,
  }), [isDesktop]);

  const sxTitleH3 = useMemo(() => ({
    alignItems: isDesktop ? 'flex-start' : 'center',
    fontWeight: 'bold',
    color: 'rgba(31,49,59,1)',
    display: '-webkit-box',
    overflow: 'hidden',
    WebkitBoxOrient: 'vertical' as const,
    WebkitLineClamp: 2,
    wordBreak: 'break-word',
    lineHeight: 1.7,
  }), [isDesktop]);

  const imgStyle = useMemo(() => ({
    width: isDesktop ? 100 : 82,
    maxWidth: isDesktop ? 100 : 82,
    height: isDesktop ? 100 : 82,
    objectFit: 'cover' as const,
    borderRadius: isDesktop ? 6 : 4,
    transition: 'transform 0.2s',
  }), [isDesktop]);

  useEffect(() => {
    if (audioRef.current) {
      const audio = audioRef.current;
      const stopId = activeStop?.id;
      const formatTime = (t: number) => {
        const m = Math.floor(t / 60);
        const s = Math.floor(t % 60);
        return `${m}:${s < 10 ? '0' : ''}${s}`;
      };
      const handleLoadedMetadata = () => {
        if (audio) {
          setAudioDuration(formatTime(audio.duration));
          if (stopId) {
            const saved = mediaProgressStore.getAudio(stopId);
            if (saved > 0) {
              audio.currentTime = saved;
              setAudioCurrentTime(formatTime(saved));
            }
          }
        }
      };
      const handleTimeUpdate = () => {
        if (audio) {
          setAudioCurrentTime(formatTime(audio.currentTime));
          if (stopId) mediaProgressStore.setAudio(stopId, audio.currentTime);
        }
      };
      const handleEnded = () => {
        setIsPlaying(false);
        setAudioCurrentTime('');
        if (stopId) mediaProgressStore.setAudio(stopId, 0);
      };
      audio.addEventListener('loadedmetadata', handleLoadedMetadata);
      audio.addEventListener('timeupdate', handleTimeUpdate);
      audio.addEventListener('ended', handleEnded);
      return () => {
        if (stopId && audio.currentTime > 0 && !audio.ended) {
          mediaProgressStore.setAudio(stopId, audio.currentTime);
        }
        audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
        audio.removeEventListener('timeupdate', handleTimeUpdate);
        audio.removeEventListener('ended', handleEnded);
      };
    }
  }, [audioRef.current]);


  const renderAudio = useMemo(() => {
    const audioMedia = activeStop?.all_media.find(media => media.type === 'audio/mpeg');

    function stopAllOtherAudios() {
      // Get all audio elements in the document
      const allAudios = document.querySelectorAll('audio');

      // Pause all audio elements except the current one
      allAudios.forEach(audio => {
        if (audio !== audioRef.current) {
          audio.pause();
        }
      });
    }

    return audioMedia && (
      <React.Fragment>
        <audio
          ref={audioRef}
          controls
          style={{ display: "none" }}
          onClick={(e) => e.stopPropagation()}
        >
          <source
            src={audioMedia.url}
            type="audio/mpeg"
          />
          {t('Your browser does not support the audio element.')}
        </audio>
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              if (isPlaying) {
                audioRef.current?.pause();
              } else {
                stopAllOtherAudios();
                audioRef.current?.play();
              }
              setIsPlaying(!isPlaying);
            }}
            size="small"
            aria-label="share"
            sx={{ p: 0 }}
          >
            {audioDuration && (
              <Typography variant="caption" sx={{ color: "rgba(31,49,59,1)", fontSize: '16px', ml: '3px', mr: '3px' }}>
                {isPlaying && audioCurrentTime ? audioCurrentTime : audioDuration}
              </Typography>
            )}
            <img src={isPlaying ? Audio : AudioSilent} alt="audio" />
          </IconButton>

        </Box>
      </React.Fragment>
    );
  }, [activeStop, t, audioRef.current, isPlaying, audioDuration, audioCurrentTime]);

  async function handleBookmarkClick() {
    const { allBookmarks, bookmarkedStops, tourState } = getAtoms();
    if (!activeStop || !tourState?.id) return;

    const tourId = tourState.id;
    const currentTourBookmarks = [...bookmarkedStops];
    const isAdding = !currentTourBookmarks.includes(activeStop.id);

    // Toggle bookmark for current stop
    if (isAdding) {
      // Add bookmark
      currentTourBookmarks.push(activeStop.id);
      setIsBookmarked(true);
      passiveCollector.trackFavoriteAdded(activeStop);
    } else {
      // Remove bookmark
      const index = currentTourBookmarks.indexOf(activeStop.id);
      if (index > -1) {
        currentTourBookmarks.splice(index, 1);
        setIsBookmarked(false);
      }
    }

    // Update allBookmarks with the new bookmarks for this tour
    setAllBookmarks({
      ...allBookmarks,
      [tourId]: currentTourBookmarks,
    });

    // Save/remove bookmark to/from backend
    const { uID } = JSON.parse(localStorage.getItem('credential_items') || '{}');
    if (uID) {
      if (isAdding) {
        await service_tourService.saveSavedStop(uID, tourId, activeStop.id);
      } else {
        await service_tourService.deleteSavedStop(uID, tourId, activeStop.id);
      }
    }
  }

  return (
    <Box
      sx={SX_OUTER_BOX}
      onClick={isSelected ? undefined : () => onSelect?.(activeStop!)}
    >
      <Box sx={sxHeaderBox}>
        <Box sx={sxTitleBg}>
          <Typography
            variant="body2"
            component="span"
            color="white"
            sx={sxTitleText}
          >
            {activeStop?.slider_item_title}
          </Typography>
        </Box>
      </Box>
      <Card
        elevation={3}
        sx={sxCard}
      >
        <CardContent
          sx={sxCardContent}
        >
          <Box sx={SX_CONTENT_ROW}>
            <Box sx={SX_CONTENT_COL}>
              <Typography variant="subtitle1" component="h3" sx={sxTitleH3}>
                {title}
              </Typography>
              {description && (
                <Typography
                  variant="body2"
                  color="text.primary"
                  sx={SX_DESCRIPTION}
                >
                  {description}
                </Typography>
              )}
              <Stack
                direction="row"
                alignItems="center"
                sx={SX_CATEGORIES_STACK}
              >
                {Array.isArray(categories) && categories.map((category, index) => (
                  index < (description ? 2 : categories.length) && (
                    <React.Fragment key={category + index}>
                      <Typography
                        variant="body2"
                        component="span"
                        sx={SX_CATEGORY_LABEL}
                      >
                        {category}
                      </Typography>
                      {index !== (description ? 1 : categories.length - 1) && (
                        <Box sx={SX_DOT_SEPARATOR} />
                      )}
                    </React.Fragment>
                  )
                ))}
                {distance && (
                  <>
                    <Typography variant="body2" component="span" color="rgba(31,49,59,1)">
                      {distance}
                    </Typography>
                    <Box sx={SX_DISTANCE_DOT} />
                  </>
                )}

                {rating !== undefined && (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography variant="body2" component="span">
                      {rating}
                    </Typography>
                    <StarIcon sx={SX_STAR} />
                  </Stack>
                )}
              </Stack>
            </Box>
            {image && (
              <img
                src={image}
                alt={title}
                style={imgStyle}
              />
            )}
          </Box>

        </CardContent>

        <Divider
          variant="fullWidth"
          sx={SX_DIVIDER}
        />

        <CardActions sx={SX_CARD_ACTIONS}>
          <Button
            onClick={(e) => {
              e.stopPropagation();
              const { activeStopState, geolocation, tourState } = getAtoms();

              // Only update state if not already the active stop
              if (activeStop && activeStopState?.id !== activeStop?.id) {
                setState_activeStopState(activeStop);
              }

              // If StopsCardsContent would render with no direction text or no image,
              // skip it and open BigStopPage directly.
              const isSmallStop = activeStop?.type?.toLowerCase() === 'smallstop';
              const hasDirectionText = !!activeStop?.diraction_text;
              const hasLocPic = !!activeStop?.loc_pics?.[0]?.url;
              const skipStopsCards = !!activeStop && !isSmallStop && (!hasDirectionText || !hasLocPic);

              if (skipStopsCards && activeStop) {
                if (tourState) {
                  passiveCollector.trackStopViewed(activeStop, isWithinRadius(geolocation.latitude, geolocation.longitude, activeStop));
                }
                setOpenedStop(activeStop);
                return;
              }

              // Track stop view (passiveCollector fires Firebase events internally)
              if (activeStop && tourState) {
                // Track small stop content view (big stops tracked on "Show me content" click)
                if (isSmallStop) {
                  passiveCollector.trackStopViewed(activeStop, isWithinRadius(geolocation.latitude, geolocation.longitude, activeStop));
                }
              }

              // Save stop modal state to localStorage for persistence
              if (tourState?.id && activeStop) {
                const stopModalStateKey = `stop_modal_state_${tourState.id}`;
                localStorage.setItem(stopModalStateKey, JSON.stringify({
                  stopId: activeStop.id,
                  modalType: 'stopsCards',
                  timestamp: new Date().toISOString()
                }));
              }

              // Then open modal
              setState_showModal(true);
              setState_modalType('stopsCards');
            }}
            size="small"
            sx={{
              lineHeight: '20px',
              fontSize: 14,
              fontWeight: 600,
              gap: '6px',
              bgcolor: 'transparent',
              color: titleColorRef.current,
              border: '1.5px solid',
              borderColor: titleColorRef.current,
              borderRadius: '6px',
              px: 1.5,
              '&:hover': {
                bgcolor: 'rgba(208, 85, 46, 0.08)',
              },
            }}
          >
            {t('new_app.explore_more')}
            <i
              className={isHebrew() ? 'fa-solid fa-angle-left' : 'fa-solid fa-angle-right'}
              aria-hidden="true"
              style={{ fontSize: 20, fontWeight: 300 }}
            />
          </Button>
          <Stack direction="row" style={{ display: 'flex', gap: 8 }}>
            {linksElements.length > 0 && (
              <IconButton onClick={(e) => {
                e.stopPropagation();
                (window as any).__skipListScroll = true;
                const { activeStopState } = getAtoms();
                // Only update state if not already the active stop
                if (activeStopState?.id !== activeStop?.id) {
                  setState_activeStopState(activeStop);
                }
                setState_modalType('linksModal');
                setState_showModal(true);
              }} size="small" aria-label="add to favorites">
                <i className="fa-solid fa-link" style={{ color: 'black', fontWeight: 400 }} aria-hidden="true"></i>
              </IconButton>
            )}
            {renderAudio}
            <IconButton onClick={(e) => {
              e.stopPropagation();
              (window as any).__skipListScroll = true;
              const { activeStopState } = getAtoms();
              // Only update state if not already the active stop
              if (activeStopState?.id !== activeStop?.id) {
                setState_activeStopState(activeStop);
              }
              if (isDesktop) {
                // Desktop: use popover navigation
                setNavState({
                  anchorEl: e.currentTarget,
                  offset: { top: 0, x: -15 },
                  usePopover: true
                });
              } else {
                // Mobile: use resizable list mode navigation
                setListMode('mobileNavigation');
              }
            }} size="small" aria-label="directions">
              <img
                src={DirectionIcon}
                alt=""
                aria-hidden="true"
                style={{ width: '21px', height: '21px' }}
              />
            </IconButton>
            <IconButton onClick={(e) => {
              e.stopPropagation();
              (window as any).__skipListScroll = true;
              const { activeStopState } = getAtoms();
              // Only update state if not already the active stop
              if (activeStopState?.id !== activeStop?.id) {
                setState_activeStopState(activeStop);
              }
              handleBookmarkClick();
            }} size="small" aria-label="add to favorites">
              <i className={isBookmarked ? "fa-solid fa-heart" : "fa-regular fa-heart"} style={{ color: isBookmarked ? '#D0552E' : 'black', fontSize: 20 }} />
            </IconButton>
          </Stack>
        </CardActions>
      </Card>
    </Box>
  );
}, (prevProps, nextProps) => {
  return prevProps.isSelected === nextProps.isSelected;
});
